<script setup lang="ts">
import type { FeatureCollection, Point } from "geojson";
import {
  type GeoJSONSource,
  type LayerSpecification,
  type LngLatLike,
  type Map,
  NavigationControl,
  Popup,
} from "maplibre-gl";
import { computed, watch } from "vue";

import BaseMap, { type BaseMapOptions } from "@/components/BaseMap.vue";
import type { Organisation } from "@/types";

const { organisations } = defineProps<{
  organisations: Organisation[];
}>();

const emit = defineEmits<{
  select: [organisation: Organisation | Organisation[]];
}>();

const organisationsCollection = computed<
  FeatureCollection<Point, Organisation>
>(() => ({
  type: "FeatureCollection",
  features: organisations.map((organisation) => ({
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: organisation.coordinates,
    },
    properties: organisation,
  })),
}));

const baseLayers: LayerSpecification[] = [
  {
    id: "background",
    type: "background",
    paint: {
      "background-color": "#D8DCE8",
    },
  },
  {
    id: "water",
    type: "fill",
    source: "prolandkort",
    "source-layer": "water",
    paint: {
      "fill-color": "#EFF5FB",
    },
  },
];

const regionsLayers: LayerSpecification[] = [
  {
    id: "regionnordjylland",
    source: "prolandkort",
    "source-layer": "regions",
    type: "fill",
    filter: ["==", ["get", "kode"], "1081"],
    paint: {
      "fill-color": "#8DA2C4",
    },
  },
  {
    id: "regionmidtjylland",
    source: "prolandkort",
    "source-layer": "regions",
    type: "fill",
    filter: ["==", ["get", "kode"], "1082"],
    paint: {
      "fill-color": "#4A6E8C",
    },
  },
  {
    id: "regionsyddanmark",
    source: "prolandkort",
    "source-layer": "regions",
    type: "fill",
    filter: ["==", ["get", "kode"], "1083"],
    paint: {
      "fill-color": "#6B8DB3",
    },
  },
  {
    id: "regionhovedsstaden",
    source: "prolandkort",
    "source-layer": "regions",
    type: "fill",
    filter: ["==", ["get", "kode"], "1084"],
    paint: {
      "fill-color": "#3A5C7E",
    },
  },
  {
    id: "regionsjaelland",
    source: "prolandkort",
    "source-layer": "regions",
    type: "fill",
    filter: ["==", ["get", "kode"], "1085"],
    paint: {
      "fill-color": "#B3CDE3",
    },
  },
];

const municipalitiesLayers: LayerSpecification[] = [
  {
    id: "municipalities",
    source: "prolandkort",
    "source-layer": "municipalities",
    type: "line",
    paint: {
      "line-color": "#000000",
      "line-opacity": 0.2,
    },
  },
];

const organisationsLayers: LayerSpecification[] = [
  // Zoom levels < 10 (Far)
  {
    id: "organisationClustersFar",
    type: "circle",
    source: "organisationsFar",
    filter: ["has", "point_count"],
    paint: {
      "circle-color": [
        "step",
        ["get", "point_count"],
        "#2ECC71", // Small clusters
        100,
        "#F5A623", // Medium clusters
        750,
        "#E74C3C", // Large clusters
      ],
      "circle-stroke-color": "#000000",
      "circle-stroke-width": 1,
      "circle-stroke-opacity": 0.1,
      "circle-radius": [
        "step",
        ["get", "point_count"],
        12, // Small clusters
        100,
        18, // Medium clusters
        750,
        24, // Large clusters
      ],
      "circle-opacity": 0.9,
    },
    maxzoom: 10,
  },
  {
    id: "organisationClusterCountFar",
    type: "symbol",
    source: "organisationsFar",
    filter: ["has", "point_count"],
    layout: {
      "text-field": "{point_count}",
      "text-font": ["Open Sans Bold"],
      "text-size": 12,
      "text-allow-overlap": true,
    },
    paint: {
      "text-color": "#FFFFFF",
      "text-halo-color": "#000000",
      "text-halo-width": 0.5,
    },
    maxzoom: 10,
  },
  {
    id: "organisationPointsFar",
    type: "symbol",
    source: "organisationsFar",
    filter: ["!", ["has", "point_count"]],
    layout: {
      "icon-image": ["get", "type"],
      "icon-size": 1 / 3,
      "icon-allow-overlap": true,
    },
    paint: {
      "icon-color": "#333333",
      "icon-halo-color": "#FFFFFF",
      "icon-halo-width": 1,
    },
    maxzoom: 10,
  },

  // Zoom levels ≥ 10 (Near)
  {
    id: "organisationClustersNear",
    type: "circle",
    source: "organisationsNear",
    filter: ["has", "point_count"],
    paint: {
      "circle-color": "#333333",
      "circle-stroke-color": "#FFFFFF",
      "circle-stroke-width": 1,
      "circle-radius": 10,
    },
    minzoom: 10,
  },
  {
    id: "organisationClusterCountNear",
    type: "symbol",
    source: "organisationsNear",
    filter: ["has", "point_count"],
    layout: {
      "text-field": "{point_count}",
      "text-font": ["Open Sans Bold"],
      "text-size": 12,
      "text-allow-overlap": true,
    },
    paint: {
      "text-color": "#FFFFFF",
      "text-halo-color": "#000000",
      "text-halo-width": 0.5,
    },
    minzoom: 10,
  },
  {
    id: "organisationPointsNear",
    type: "symbol",
    source: "organisationsNear",
    filter: ["!", ["has", "point_count"]],
    layout: {
      "icon-image": ["get", "type"],
      "icon-size": 1 / 3,
      "icon-allow-overlap": true,
    },
    paint: {
      "icon-color": "#333333",
      "icon-halo-color": "#FFFFFF",
      "icon-halo-width": 1,
    },
    minzoom: 10,
  },
];

const options: BaseMapOptions = {
  style: {
    version: 8,
    sprite: `${window.location.origin}/map/sprite`,
    glyphs: `${window.location.origin}/map/fonts/pbf/{fontstack}/{range}.pbf`,
    sources: {
      prolandkort: {
        type: "vector",
        url: `pmtiles://${window.location.origin}/map/prolandkort.pmtiles`,
      },
      organisationsNear: {
        type: "geojson",
        data: organisationsCollection.value,
        cluster: true,
        clusterRadius: 15,
      },
      organisationsFar: {
        type: "geojson",
        data: organisationsCollection.value,
        cluster: true,
        clusterRadius: 25,
      },
    },
    layers: [
      ...baseLayers,
      ...regionsLayers,
      ...municipalitiesLayers,
      ...organisationsLayers,
    ],
  },
  bounds: [
    [7.536621, 54.406772],
    [15.534668, 57.828302],
  ],
  maxBounds: [
    [0.0043480173, 51.40677199999962],
    [22.2082083002, 60.5161418951638],
  ],
  maxZoom: 10,
  dragRotate: false,
  touchPitch: false,
  attributionControl: {
    compact: true,
    customAttribution: [
      '<a href="https://www.openstreetmap.org/copyright">© OpenStreetMap</a>',
      '<a href="https://dataforsyningen.dk/data" target="_blank">Klimadatastyrelsen</a> <a href="https://creativecommons.org/licenses/by/4.0/deed.da" target="_blank">CC BY 4.0</a>',
    ],
  },
};

function getGeoJsonSource(map: Map, sourceId: string) {
  const source = map.getSource(sourceId);

  if (!source) {
    throw new Error(`Source with id ${sourceId} not found`);
  }

  if (source.type !== "geojson") {
    throw new Error(`Source with id ${sourceId} is not a GeoJSON source`);
  }

  return source as GeoJSONSource;
}

const clusterPopupLimit = 10;

async function getClusterOrganisations(map: Map, clusterId: number) {
  const features = await getGeoJsonSource(
    map,
    "organisationsNear",
  ).getClusterLeaves(clusterId, Infinity, 0);

  return features.map(({ properties }) => properties as Organisation);
}

function buildPopupContent(title: string | null | undefined) {
  const content = document.createElement("div");

  if (title) {
    const heading = document.createElement("strong");

    heading.textContent = title;
    content.append(heading);
  }

  return content;
}

function buildOrganisationPopupContent(organisation: Organisation) {
  const content = buildPopupContent(organisation.topParentName);
  const name = document.createElement("div");

  name.textContent = organisation.name;
  content.append(name);

  return content;
}

function buildClusterPopupContent(clusterOrganisations: Organisation[]) {
  const content = buildPopupContent(clusterOrganisations[0]?.aggregatorName);
  const list = document.createElement("ul");

  clusterOrganisations.slice(0, clusterPopupLimit).forEach((organisation) => {
    const item = document.createElement("li");

    item.textContent = organisation.name;
    list.append(item);
  });

  content.append(list);

  const remaining = clusterOrganisations.length - clusterPopupLimit;

  if (remaining > 0) {
    const more = document.createElement("div");

    more.textContent = `og ${remaining} flere`;
    content.append(more);
  }

  return content;
}

function onLoaded(map: Map) {
  map.addControl(
    new NavigationControl({
      showCompass: false,
      showZoom: true,
    }),
    "bottom-right",
  );

  map.touchZoomRotate.disableRotation();
  map.keyboard.disableRotation();

  map.on(
    "mouseover",
    [
      "organisationClustersFar",
      "organisationPointsFar",
      "organisationClustersNear",
      "organisationPointsNear",
    ],
    (event) => {
      event.target.getCanvas().style.cursor = "pointer";
    },
  );

  map.on(
    "mouseout",
    [
      "organisationClustersFar",
      "organisationPointsFar",
      "organisationClustersNear",
      "organisationPointsNear",
    ],
    (event) => {
      event.target.getCanvas().style.cursor = "";
    },
  );

  const popup = new Popup({
    className: "organisation-popup",
    closeButton: false,
    closeOnClick: false,
    maxWidth: "320px",
    offset: 12,
  });
  let hoveredFeatureKey: string | undefined;

  function hidePopup() {
    hoveredFeatureKey = undefined;
    popup.remove();
  }

  function showPopup(
    featureKey: string,
    coordinates: LngLatLike,
    content: HTMLElement,
  ) {
    if (featureKey === hoveredFeatureKey) {
      popup.setLngLat(coordinates).setDOMContent(content).addTo(map);
    }
  }

  map.on("mousemove", "organisationClustersNear", async (event) => {
    const feature = event.features?.[0];

    if (!feature || feature.geometry.type !== "Point") {
      return;
    }

    const clusterId: number = feature.properties.cluster_id;
    const featureKey = `cluster:${clusterId}`;

    if (featureKey === hoveredFeatureKey) {
      return;
    }

    hoveredFeatureKey = featureKey;

    const clusterOrganisations = await getClusterOrganisations(map, clusterId);

    showPopup(
      featureKey,
      feature.geometry.coordinates as LngLatLike,
      buildClusterPopupContent(clusterOrganisations),
    );
  });

  map.on(
    "mousemove",
    ["organisationPointsFar", "organisationPointsNear"],
    (event) => {
      const feature = event.features?.[0];

      if (!feature || feature.geometry.type !== "Point") {
        return;
      }

      const organisation = feature.properties as Organisation;
      const featureKey = `organisation:${organisation.id}`;

      if (featureKey === hoveredFeatureKey) {
        return;
      }

      hoveredFeatureKey = featureKey;
      showPopup(
        featureKey,
        feature.geometry.coordinates as LngLatLike,
        buildOrganisationPopupContent(organisation),
      );
    },
  );

  map.on(
    "mouseout",
    [
      "organisationClustersNear",
      "organisationPointsFar",
      "organisationPointsNear",
    ],
    hidePopup,
  );

  map.on("click", "organisationClustersFar", async (event) => {
    const feature = event.features?.[0];

    if (feature && feature.geometry.type === "Point") {
      const source = getGeoJsonSource(map, "organisationsFar");
      const zoom = await source.getClusterExpansionZoom(
        feature.properties.cluster_id,
      );
      const center = feature.geometry.coordinates as LngLatLike;

      map.easeTo({ center, zoom });
    }
  });

  map.on("click", "organisationClustersNear", async (event) => {
    const feature = event.features?.[0];

    if (feature && feature.geometry.type === "Point") {
      hidePopup();
      emit(
        "select",
        await getClusterOrganisations(map, feature.properties.cluster_id),
      );
    }
  });

  map.on(
    "click",
    ["organisationPointsFar", "organisationPointsNear"],
    (event) => {
      const feature = event.features?.[0];

      if (feature) {
        hidePopup();
        emit("select", feature.properties as Organisation);
      }
    },
  );

  watch(organisationsCollection, async (data) => {
    await Promise.all(
      ["organisationsFar", "organisationsNear"].map((sourceId) =>
        getGeoJsonSource(map, sourceId).setData(data),
      ),
    );
  });
}
</script>

<template>
  <BaseMap :initial-options="options" @loaded="(map) => onLoaded(map)" />
</template>

<style lang="scss">
.organisation-popup ul {
  margin: 0.25rem 0 0;
  padding-left: 1.25rem;
}
</style>
